import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";

interface CaseStudyData {
  seoTitle: string;
  metaDescription: string;
  h1: string;
  imageAlt: string;
  focusKeywords: string;
  challenge: string;
  solution: string[];
  results: string;
  whyItWorks: string[];
  ctaText: string;
  relatedSlugs: string[];
}

const caseStudies: Record<string, CaseStudyData> = {
  "novotel-london-excel-laundry-partnership": {
    seoTitle: "Novotel London Excel Laundry Case Study | Xcel Dry Cleaners",
    metaDescription:
      "How Xcel Dry Cleaners provides same-day guest & staff laundry for Novotel London Excel, collected every morning and delivered back that evening.",
    h1: "Novotel London Excel: A Same-Day Guest & Staff Laundry Partnership",
    imageAlt:
      "Xcel Dry Cleaners van collecting laundry outside Novotel London Excel",
    focusKeywords:
      "Novotel London Excel laundry, hotel laundry service London, guest laundry service, staff uniform laundry, same-day hotel laundry",
    challenge:
      "Novotel London Excel needed a laundry partner who could keep pace with a busy events calendar around ExCeL London. Guests expect fast, reliable laundry returns, and housekeeping and front-of-house teams need a constant supply of clean, presentable uniforms, without the hotel having to run its own laundry operation or hold excess uniform stock.",
    solution: [
      "Xcel Dry Cleaners now manages both guest laundry and staff uniform care for Novotel London Excel. The service runs on a simple daily rhythm: items are collected each morning directly from the hotel, professionally cleaned and pressed during the day, and delivered back that same evening. This same-day collection-and-delivery model means the hotel never needs to worry about laundry backlogs, even during peak occupancy.",
      "The service covers guest dry cleaning and shirt laundering through to bulk staff uniform washing, pressing and folding, all handled to the same quality standard Xcel applies across every customer.",
    ],
    results:
      "Since the partnership began, Xcel Dry Cleaners has maintained 100% positive feedback from Novotel London Excel. Staff consistently have clean uniforms ready for every shift, and guest laundry requests are returned the same day, a detail that regularly shows up in positive guest reviews. Reliable morning collection and evening delivery means hotel staff can focus on guests rather than laundry logistics.",
    whyItWorks: [
      "Same-day collection and delivery, every day of the week",
      "One consistent quality standard across guest laundry and staff uniforms",
      "No need to hold excess uniform stock or run an in-house laundry team",
      "Flexible capacity during conference and event periods around ExCeL London",
      "A single point of contact for scheduling, queries and quality assurance",
    ],
    ctaText:
      "Looking for a reliable hotel laundry partner in London? Contact Xcel Dry Cleaners to discuss a same-day collection and delivery schedule for your property.",
    relatedSlugs: [
      "crowne-plaza-london-docklands-laundry-partnership",
      "ibis-london-excel-docklands-laundry-partnership",
    ],
  },
  "crowne-plaza-london-docklands-laundry-partnership": {
    seoTitle:
      "Crowne Plaza London Docklands Laundry Case Study | Xcel Dry Cleaners",
    metaDescription:
      "How Xcel Dry Cleaners provides same-day guest & staff laundry for Crowne Plaza London Docklands by IHG, collected every morning and delivered that evening.",
    h1: "Crowne Plaza London Docklands by IHG: A Same-Day Guest & Staff Laundry Partnership",
    imageAlt:
      "Xcel Dry Cleaners van collecting laundry outside Crowne Plaza London Docklands by IHG",
    focusKeywords:
      "Crowne Plaza London Docklands laundry, hotel laundry service London, guest laundry service, staff uniform laundry, same-day hotel laundry",
    challenge:
      "As a large IHG property serving both business and leisure guests, Crowne Plaza London Docklands needed a laundry partner capable of handling high volumes without compromising on turnaround or quality. In-house laundry would have meant significant space, equipment and staffing costs, resources better spent on guest-facing operations.",
    solution: [
      "Xcel Dry Cleaners was brought in to manage guest laundry and staff uniform care for the hotel. Laundry is collected each morning, professionally cleaned and pressed during the day, and delivered back that same evening. This same-day model gives the hotel the reassurance of a fast, predictable turnaround regardless of daily volume.",
      "The service spans everything from individual guest dry cleaning and shirt laundering to bulk uniform washing, pressing and folding for housekeeping, front-of-house and food and beverage teams, all delivered to one consistent quality standard.",
    ],
    results:
      "The partnership has delivered 100% positive feedback from Crowne Plaza London Docklands to date. Staff uniforms are ready for every shift, and guest laundry is consistently returned the same day, supporting the high service standards expected of an IHG property. The reliability of morning collection and evening delivery has removed laundry logistics as a daily concern for hotel management.",
    whyItWorks: [
      "Same-day collection and delivery, every day of the week",
      "Consistent quality across both guest laundry and staff uniforms",
      "No need to hold excess uniform stock or manage an in-house laundry team",
      "Capacity that flexes with occupancy and event-driven demand near ExCeL London",
      "A single point of contact for scheduling, queries and quality assurance",
    ],
    ctaText:
      "Looking for a reliable hotel laundry partner in London? Contact Xcel Dry Cleaners to discuss a same-day collection and delivery schedule for your property.",
    relatedSlugs: [
      "novotel-london-excel-laundry-partnership",
      "ibis-london-excel-docklands-laundry-partnership",
    ],
  },
  "ibis-london-excel-docklands-laundry-partnership": {
    seoTitle:
      "ibis London Excel Docklands Laundry Case Study | Xcel Dry Cleaners",
    metaDescription:
      "How Xcel Dry Cleaners provides same-day guest & staff laundry for ibis London Excel Docklands, collected every morning and delivered back that evening.",
    h1: "ibis London Excel Docklands: A Same-Day Guest & Staff Laundry Partnership",
    imageAlt:
      "Xcel Dry Cleaners van collecting laundry outside ibis London Excel Docklands",
    focusKeywords:
      "ibis London Excel Docklands laundry, hotel laundry service London, guest laundry service, staff uniform laundry, same-day hotel laundry",
    challenge:
      "ibis London Excel Docklands needed a cost-effective way to keep guest laundry and staff uniforms consistently clean and on time, without adding the overhead of an in-house laundry operation. As a high-volume, budget-friendly hotel, efficiency and reliability were the priority.",
    solution: [
      "Xcel Dry Cleaners now handles guest laundry and staff uniform care for the hotel on a same-day basis: laundry is collected each morning, cleaned and pressed during the day, and delivered back that evening, ready for the next shift or guest checkout. This straightforward model fits the hotel's fast-paced daily operations without requiring any extra space or equipment on-site.",
      "The service covers guest dry cleaning and shirt laundering through to bulk staff uniform washing, pressing and folding, handled with the same quality control Xcel applies across all its hotel and retail customers.",
    ],
    results:
      "Since the partnership began, ibis London Excel Docklands has given Xcel Dry Cleaners 100% positive feedback. Staff have clean uniforms ready for every shift, and guest laundry is consistently returned the same day. Reliable, predictable collection and delivery has taken laundry logistics off the hotel team's daily to-do list.",
    whyItWorks: [
      "Same-day collection and delivery, every day of the week",
      "Consistent quality across guest laundry and staff uniforms",
      "No need to hold excess uniform stock or manage an in-house laundry team",
      "Capacity that scales with occupancy around ExCeL London events",
      "A single point of contact for scheduling, queries and quality assurance",
    ],
    ctaText:
      "Looking for a reliable hotel laundry partner in London? Contact Xcel Dry Cleaners to discuss a same-day collection and delivery schedule for your property.",
    relatedSlugs: [
      "novotel-london-excel-laundry-partnership",
      "crowne-plaza-london-docklands-laundry-partnership",
    ],
  },
};

const slugToName: Record<string, string> = {
  "novotel-london-excel-laundry-partnership": "Novotel London Excel",
  "crowne-plaza-london-docklands-laundry-partnership":
    "Crowne Plaza London Docklands by IHG",
  "ibis-london-excel-docklands-laundry-partnership":
    "ibis London Excel Docklands",
};

function CaseStudyPage() {
  const { slug } = useParams<{ slug: string }>();
  const data = slug ? caseStudies[slug] : undefined;

  useEffect(() => {
    if (!data) return;
    document.title = data.seoTitle;
    document.getElementById("title")?.setAttribute("content", data.seoTitle);
    document
      .getElementById("description")
      ?.setAttribute("content", data.metaDescription);
  }, [data]);

  if (!data) return <Navigate to="/404" replace />;

  return (
    <>
      <div className="xcel-page-hero">
        <div className="container">
          <nav className="xcel-breadcrumb" aria-label="breadcrumb">
            <a href="/">Home</a>
            <span aria-hidden="true"> / </span>
            <a href="/case-studies">Case Studies</a>
            <span aria-hidden="true"> / </span>
            <span aria-current="page">{slugToName[slug!]}</span>
          </nav>
          <h1>{data.h1}</h1>
          <p className="xcel-page-hero-meta">
            Hotel laundry partnership &nbsp;·&nbsp; ExCeL London
          </p>
        </div>
      </div>

      <div className="xcel-case-study">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <section className="xcel-cs-section">
                <h2 className="xcel-cs-section-title">The Challenge</h2>
                <p>{data.challenge}</p>
              </section>

              <section className="xcel-cs-section">
                <h2 className="xcel-cs-section-title">The Solution</h2>
                {data.solution.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </section>

              <section className="xcel-cs-section">
                <h2 className="xcel-cs-section-title">The Results</h2>
                <p>{data.results}</p>
              </section>

              <section className="xcel-cs-section">
                <h2 className="xcel-cs-section-title">Why It Works</h2>
                <ul className="xcel-why-list">
                  {data.whyItWorks.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </section>

              <div className="xcel-cta-banner">
                <h3>{data.ctaText}</h3>
                <Link to="/Contactus" className="xcel-cta-btn">
                  Contact Us
                </Link>
              </div>
            </div>

            <aside className="col-lg-4">
              <div className="xcel-cs-sidebar">
                <h3 className="xcel-cs-sidebar-title">Other Hotel Partnerships</h3>
                <ul className="xcel-cs-related-list">
                  {data.relatedSlugs.map((s) => (
                    <li key={s}>
                      <Link to={`/case-studies/${s}`}>{slugToName[s]}</Link>
                    </li>
                  ))}
                </ul>

                <div className="xcel-cs-sidebar-stat">
                  <span className="xcel-stat-number">100%</span>
                  <span className="xcel-stat-label">
                    Positive feedback across all partner hotels
                  </span>
                </div>

                <div className="xcel-cs-sidebar-info">
                  <h4>Service Includes</h4>
                  <ul>
                    <li>Guest dry cleaning</li>
                    <li>Shirt laundering</li>
                    <li>Staff uniform washing &amp; pressing</li>
                    <li>Same-day collection &amp; delivery</li>
                  </ul>
                </div>

                <div className="xcel-cs-sidebar-cta">
                  <p>Interested in a hotel partnership?</p>
                  <Link to="/Contactus" className="xcel-sidebar-btn">
                    Get in Touch
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <div className="xcel-blog-links-strip">
        <div className="container text-center">
          <p className="mb-2">
            <strong>Learn more about hotel laundry outsourcing</strong>
          </p>
          <div className="xcel-blog-strip-links">
            <Link to="/blog/hotel-laundry-outsourcing-london">
              Why Hotels Are Outsourcing Laundry
            </Link>
            <Link to="/blog/commercial-cleaning-services-london">
              Commercial Laundry for Businesses
            </Link>
            <Link to="/blog/professional-garment-care-guide">
              Professional Garment Care Guide
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

export default CaseStudyPage;
